// Wave 2 — Cross-source price comparison.
// Truthful: never claims a "live" best price; every row is a snapshot with observed_at.
import React, { useState } from "react";
import AppShell from "../../components/AppShell";
import { api, formatApiError } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Search, ShieldCheck } from "lucide-react";

const SOURCE_LABELS = {
  vendor_portal: "Vendor Portal",
  manual_upload: "Manual Upload",
  marketplace_research: "Marketplace Research",
  home_depot_pro: "Home Depot Pro",
  lowes_pro: "Lowe's Pro",
  licensed_provider: "Licensed Provider",
  direct_supplier: "Direct Supplier",
};

export default function MaterialPriceCompare() {
  const [product, setProduct] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  async function search() {
    if (!product.trim()) return;
    setLoading(true);
    try {
      const r = await api.get(`/material-prices/compare`, { params: { product } });
      setResult(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Search failed");
    } finally {
      setLoading(false);
    }
  }

  const money = (n, cur) => `${cur || "USD"} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <AppShell
      title="Compare across sources"
      subtitle="Same product · every recorded observation, cheapest first. Never a live purchasable price unless the source is a real API within its observation window."
    >
      <div className="flex items-center gap-2 mb-4 max-w-2xl">
        <Input
          value={product}
          onChange={(e) => setProduct(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") search(); }}
          placeholder="Product description (substring, case-insensitive)"
          data-testid="mp-compare-product"
        />
        <Button onClick={search} disabled={loading || !product.trim()} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="mp-compare-search">
          <Search className="w-4 h-4 mr-1.5" /> {loading ? "Searching…" : "Compare"}
        </Button>
      </div>

      {result && result.count === 0 && (
        <Card className="border-erf-borderCream" data-testid="mp-compare-empty">
          <CardContent className="p-8 text-center text-erf-dark/60 font-nav text-sm">
            No recorded observations match "{result.product}". Try a shorter substring, or record a price first.
          </CardContent>
        </Card>
      )}

      {result && result.count > 0 && (
        <>
          <Card className="border-erf-borderCream mb-3" data-testid="mp-compare-summary">
            <CardContent className="p-4 grid grid-cols-4 gap-3">
              <div>
                <div className="text-[10px] font-mono uppercase text-erf-dark/60">Observations</div>
                <div className="font-mono text-xl">{result.summary.count}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase text-erf-dark/60">Low</div>
                <div className="font-mono text-xl">{money(result.summary.min)}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase text-erf-dark/60">High</div>
                <div className="font-mono text-xl">{money(result.summary.max)}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase text-erf-dark/60">Sources</div>
                <div className="text-xs font-nav">{result.summary.sources.map((s) => SOURCE_LABELS[s] || s).join(", ")}</div>
              </div>
              <div className="col-span-4 text-[11px] font-nav text-erf-dark/60 border-t border-erf-borderCream pt-3">
                {result.summary.note}
              </div>
            </CardContent>
          </Card>
          <div className="grid gap-2" data-testid="mp-compare-rows">
            {result.rows.map((r, i) => (
              <Card key={r.id} className={`border-erf-borderCream ${i === 0 ? "ring-2 ring-erf-gold/30" : ""}`} data-testid={`mp-compare-row-${r.id}`}>
                <CardContent className="p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover">{SOURCE_LABELS[r.source_type] || r.source_type}</span>
                      <span className="text-[10px] font-mono text-erf-dark/50">{r.observed_at?.slice(0, 10)}</span>
                      {r.audit_entry_id != null && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono text-erf-dark/60">
                          <ShieldCheck className="w-3 h-3" /> #{r.audit_entry_id}
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-nav text-erf-dark truncate">{r.product_description}</div>
                    <div className="text-[11px] font-mono text-erf-dark/60 truncate">{r.store_or_supplier}{r.supplier_location ? ` · ${r.supplier_location}` : ""}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-lg text-erf-dark">{money(r.price_per_unit, r.currency)}</div>
                    <div className="text-[10px] font-mono uppercase text-erf-dark/60">per {r.unit_of_measure}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </AppShell>
  );
}
